/* eslint-disable @typescript-eslint/no-shadow */
import dayjs from 'dayjs';
import _, { isNumber } from 'lodash';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import constants from 'src/utils/constants';
dayjs.extend(advancedFormat);

const CUSTOM_KEYS = ['backorder_allowed', 'on_order_details', 'in_transit_details', 'notes'];
const SPECIAL_KEYS = ['available_quantity', 'reserved_quantity', 'in_stock_quantity', 'total_reserved'];
export const INVENTORY_STATUS = {
	out_of_stock: 'OUT_OF_STOCK',
	back_order: 'BACKORDER',
	in_stock: 'IN_STOCK',
};
export const INVENTORY_ICON_META = {
	available_quantity: {
		label: 'Available',
		show: true,
	},
	reserved_quantity: {
		label: 'Reserved',
		show: true,
	},
	in_stock_quantity: {
		label: 'In stock',
		show: true,
	},
	backorder_allowed: {
		label: 'Back order allowed',
		show: true,
	},
	in_transit_details: {
		label: 'In transit',
		show: true,
		entries: 5,
	},
	on_order_details: {
		label: 'On order',
		show: true,
		entries: 5,
	},
	notes: {
		label: 'Notes',
		show: true,
	},
};
const handle_transform_value = (inventoryData: any, data: any) => {
	const updated_data = _.take(_.sortBy(inventoryData, 'date'), data?.entries);

	return _.map(updated_data, (item: any) => {
		const date = item?.date ? dayjs(item?.date * 1000)?.format("Do MMM'YY") : null;
		const quantity = item?.quantity || 0;

		const item_info = date ? `${quantity} units arriving by ${date}` : `${quantity} units arriving soon`;

		return item_info;
	});
};

const transformData = (inventoryData: any, metaData: any, reserved_quantity: number) => {
	return _.map(metaData, (value, key) => {
		let transformedValue = inventoryData?.[key] || 0;
		let is_show = true;

		switch (key) {
			case 'backorder_allowed':
				const backorder_quantity = inventoryData?.backorder_available_quantity || 0;
				transformedValue = inventoryData[key] ? (backorder_quantity !== 0 ? `upto ${backorder_quantity} units` : '') : '';
				break;

			case 'on_order_details':
			case 'in_transit_details':
				const filter_data = _.filter(inventoryData[key], (item: any) => item?.quantity > 0);
				transformedValue = handle_transform_value(filter_data, value);
				is_show = !_.isEmpty(filter_data);
				break;

			case 'reserved_quantity':
				transformedValue = inventoryData?.[key] - reserved_quantity || 0;
				break;

			case 'total_available':
				transformedValue = inventoryData?.[key] + reserved_quantity || 0;
				break;

			default:
				break;
		}

		return {
			data: {
				label: value.label,
				value: _.includes(SPECIAL_KEYS, key) ? `${transformedValue}` : transformedValue,
			},
			is_custom: _.includes(CUSTOM_KEYS, key),
			id: key,
			entries: value?.entries,
			is_notes: key === 'notes',
			is_shown: _.includes(SPECIAL_KEYS, key) ? value?.show : value?.show && is_show,
		};
	});
};

export const handle_get_menu = (inventory_data: any, meta: any, reserved: number) => {
	const menu = transformData(inventory_data, meta, reserved);

	return menu.filter((item: any) => item?.is_shown && item?.data?.value);
};

export function get_earliest_backorder_date(inventory_data: any) {
	// Create an array to hold all the relevant dates
	let date_array = [];
	// Helper function to convert a timestamp into a Day.js object
	function convert_to_dayjs(timestamp: number) {
		if (!timestamp) return null;
		return isNumber(timestamp) ? dayjs.unix(timestamp) : dayjs(timestamp);
	}

	// Helper function to add dates from a list of items
	function add_dates_from_items(items: any) {
		if (_.isEmpty(items)) return;
		items?.forEach((item: any) => {
			if (item?.date) {
				const date = convert_to_dayjs(item?.date);
				if (date) date_array.push(date);
			}
		});
	}

	// Add on_order_quantity and on_order_date to the date_array
	if (inventory_data?.on_order_quantity > 0 && inventory_data?.on_order_date) {
		const date = convert_to_dayjs(inventory_data.on_order_date);
		if (date) date_array.push(date);
	}

	// Add on_order_details dates
	add_dates_from_items(inventory_data?.on_order_details);

	// Add in_transit_details dates
	add_dates_from_items(inventory_data?.in_transit_details);

	// If the date_array is empty, no valid date found
	if (date_array.length === 0) {
		return null;
	}

	// Find the earliest date using Day.js comparison
	let earliest_date = date_array?.[0];

	for (let i = 1; i < date_array.length; i++) {
		if (date_array?.[i].isBefore(earliest_date, 'day')) {
			earliest_date = date_array?.[i];
		}
	}

	return earliest_date?.format(constants.CUSTOM_DATE_FORMATS.DATE_Do_MMM_YY);
}

export const get_inventory_label_from_status = (
	inventory_status: (typeof INVENTORY_STATUS)[keyof typeof INVENTORY_STATUS],
	inventory_i_button_config: any,
) => {
	switch (inventory_status) {
		case INVENTORY_STATUS.in_stock: {
			return inventory_i_button_config?.main_available_status?.label ?? inventory_status;
		}
		case INVENTORY_STATUS.back_order: {
			return inventory_i_button_config?.main_backorder?.label ?? inventory_status;
		}
		case INVENTORY_STATUS.out_of_stock: {
			return inventory_i_button_config?.main_out_of_stock?.label ?? inventory_status;
		}
		default: {
			return inventory_status;
		}
	}
};
